import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Send, Plus, MessageSquare, Trash2, ChevronRight } from 'lucide-react'
import {
  PageHeader, Card, CardBody, Button, LoadingSpinner, ErrorMessage,
  EmptyState, ConfirmationModal
} from '../components/common'
import { knowledgeBaseApi } from '../services/api'
import { extractErrorMessage } from '../utils/errorUtils'
import MarkdownContent from '../components/chat/MarkdownContent'
import SourceCard from '../components/chat/SourceCard'
import PdfViewerModal from '../components/chat/PdfViewerModal'

function deduplicateCitations(citations) {
  if (!citations) return []
  const seen = new Map()
  for (const c of citations) {
    const key = c.documentS3Key || c.snippet?.substring(0, 50) || String(Math.random())
    if (!seen.has(key)) seen.set(key, c)
  }
  return Array.from(seen.values())
}

function ChatMessage({ message, onCitationClick }) {
  const isUser = message.role === 'user'
  const dedupedCitations = deduplicateCitations(message.citations)

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${isUser ? 'bg-primary-500 text-white' : 'bg-slate-100 text-slate-900'}`}>
        {isUser ? (
          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        ) : (
          <MarkdownContent>{message.content}</MarkdownContent>
        )}

        {/* Citations */}
        {dedupedCitations.length > 0 && (
          <div className="mt-3 pt-2 border-t border-slate-200">
            <p className="text-xs font-medium mb-2 text-slate-500">Sources:</p>
            <div className="space-y-2">
              {dedupedCitations.map((citation, i) => (
                <SourceCard key={i} citation={citation} onClick={onCitationClick} />
              ))}
            </div>
          </div>
        )}

        <p className={`text-[10px] mt-1 ${isUser ? 'text-primary-200' : 'text-slate-400'}`}>
          {new Date(message.createdAt).toLocaleTimeString()}
        </p>
      </div>
    </div>
  )
}

export default function KnowledgeChat() {
  const { id: sessionIdParam } = useParams()
  const navigate = useNavigate()
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  const [sessions, setSessions] = useState([])
  const [currentSession, setCurrentSession] = useState(null)
  const [messages, setMessages] = useState([])
  const [query, setQuery] = useState('')
  const [sending, setSending] = useState(false)
  const [loadingSessions, setLoadingSessions] = useState(true)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [error, setError] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [sessionToDelete, setSessionToDelete] = useState(null)
  const [pdfViewer, setPdfViewer] = useState({ isOpen: false, s3Key: null, documentName: null, pageNumber: null })

  const buildingId = localStorage.getItem('selectedBuildingId')

  useEffect(() => {
    if (buildingId) loadSessions()
  }, [buildingId])

  useEffect(() => {
    if (sessionIdParam) loadSession(parseInt(sessionIdParam))
  }, [sessionIdParam])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const loadSessions = async () => {
    try {
      setLoadingSessions(true)
      const response = await knowledgeBaseApi.getSessions({ buildingId, PageSize: 50 })
      setSessions(response.data.data || [])
    } catch (err) {
      setError(extractErrorMessage(err))
    } finally {
      setLoadingSessions(false)
    }
  }

  const loadSession = async (sessionId) => {
    try {
      setLoadingMessages(true)
      const response = await knowledgeBaseApi.getSession(sessionId)
      setCurrentSession(response.data)
      setMessages(response.data.messages || [])
    } catch (err) {
      setError(extractErrorMessage(err))
    } finally {
      setLoadingMessages(false)
    }
  }

  const startNewSession = () => {
    setCurrentSession(null)
    setMessages([])
    navigate('/knowledge-base/chat')
    inputRef.current?.focus()
  }

  const handleSend = async () => {
    if (!query.trim() || sending || !buildingId) return
    const userQuery = query.trim()
    setQuery('')
    setSending(true)
    setError(null)

    // Optimistic user message
    const tempUserMsg = { role: 'user', content: userQuery, createdAt: new Date().toISOString() }
    setMessages(prev => [...prev, tempUserMsg])

    try {
      const response = await knowledgeBaseApi.chat({
        buildingId,
        query: userQuery,
        sessionId: currentSession?.id || null
      })

      const data = response.data

      // If new session was created, update navigation and session list
      if (!currentSession) {
        setCurrentSession({ id: data.sessionId, title: data.sessionTitle })
        navigate(`/knowledge-base/chat/${data.sessionId}`, { replace: true })
        loadSessions()
      }

      // Add assistant response
      const assistantMsg = {
        role: 'assistant',
        content: data.answer,
        citations: data.citations,
        followUpQuestions: data.followUpQuestions,
        createdAt: new Date().toISOString()
      }
      setMessages(prev => [...prev, assistantMsg])
    } catch (err) {
      setError(extractErrorMessage(err))
      // Remove optimistic message on error
      setMessages(prev => prev.slice(0, -1))
    } finally {
      setSending(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleDeleteSession = async () => {
    if (!sessionToDelete) return
    try {
      await knowledgeBaseApi.deleteSession(sessionToDelete.id)
      if (currentSession?.id === sessionToDelete.id) {
        startNewSession()
      }
      setShowDeleteModal(false)
      setSessionToDelete(null)
      loadSessions()
    } catch (err) {
      setError(extractErrorMessage(err))
    }
  }

  const handleOpenCitation = (citation) => {
    const key = citation.documentS3Key
    const name = citation.documentName || key?.split('/').pop()
    const ext = name?.split('.').pop()?.toLowerCase()

    if (ext === 'pdf') {
      setPdfViewer({ isOpen: true, s3Key: key, documentName: name, pageNumber: citation.pageNumber })
    } else if (key) {
      // Non-PDF: open presigned URL in new tab
      knowledgeBaseApi.getPresignedUrl(key)
        .then(res => window.open(res.data.url, '_blank'))
        .catch(() => {})
    }
  }

  // Get the last assistant message's follow-up questions
  const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant')
  const followUps = lastAssistant?.followUpQuestions || []

  return (
    <div className="space-y-6">
      <PageHeader
        title="Knowledge Base Chat"
        subtitle="Ask questions about your building documents"
        backLink={{ label: 'Back to Knowledge Base', href: '/knowledge-base' }}
      />

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

      <div className="flex gap-6 h-[calc(100vh-220px)]">
        {/* Sidebar: Sessions */}
        <div className="w-64 flex-shrink-0 flex flex-col">
          <Button variant="primary" icon={<Plus size={16} />} fullWidth onClick={startNewSession} className="mb-3">
            New Chat
          </Button>
          <div className="flex-1 overflow-y-auto space-y-1">
            {loadingSessions ? <LoadingSpinner /> : sessions.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No chat history</p>
            ) : sessions.map(session => (
              <div key={session.id}
                className={`flex items-center justify-between p-2 rounded-lg cursor-pointer group
                  ${currentSession?.id === session.id ? 'bg-primary-50 text-primary-700' : 'hover:bg-slate-50 text-slate-700'}`}
                onClick={() => { navigate(`/knowledge-base/chat/${session.id}`); loadSession(session.id) }}>
                <div className="flex items-center gap-2 min-w-0">
                  <MessageSquare size={14} className="flex-shrink-0" />
                  <span className="text-sm truncate">{session.title || 'Untitled'}</span>
                </div>
                <button onClick={(e) => { e.stopPropagation(); setSessionToDelete(session); setShowDeleteModal(true) }}
                  className="p-0.5 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Main: Chat */}
        <div className="flex-1 flex flex-col">
          <Card className="flex-1 flex flex-col overflow-hidden">
            <CardBody className="flex-1 overflow-y-auto p-4">
              {loadingMessages ? <LoadingSpinner /> : messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <EmptyState
                    title="Start a conversation"
                    description="Ask questions about your building's documents and get AI-powered answers with citations"
                  />
                </div>
              ) : (
                <div>
                  {messages.map((msg, i) => <ChatMessage key={i} message={msg} onCitationClick={handleOpenCitation} />)}
                  {sending && (
                    <div className="flex justify-start mb-4">
                      <div className="bg-slate-100 rounded-2xl px-4 py-3">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </CardBody>

            {/* Follow-up suggestions */}
            {followUps.length > 0 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2">
                {followUps.map((q, i) => (
                  <button key={i} onClick={() => { setQuery(q); inputRef.current?.focus() }}
                    className="text-xs text-primary-600 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-full flex items-center gap-1 transition-colors">
                    <ChevronRight size={10} /> {q}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="border-t border-slate-200 p-4">
              <div className="flex gap-3">
                <textarea ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask a question about your building documents..."
                  className="flex-1 resize-none rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/30"
                  rows={1} disabled={sending} />
                <Button variant="primary" icon={<Send size={16} />} onClick={handleSend}
                  disabled={!query.trim() || sending} loading={sending}>
                  Send
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setSessionToDelete(null) }}
        onConfirm={handleDeleteSession}
        title="Delete Chat Session"
        message={`Delete "${sessionToDelete?.title || 'this session'}" and all its messages?`}
        confirmLabel="Delete"
        variant="danger"
      />

      <PdfViewerModal
        isOpen={pdfViewer.isOpen}
        onClose={() => setPdfViewer({ isOpen: false, s3Key: null, documentName: null, pageNumber: null })}
        s3Key={pdfViewer.s3Key}
        documentName={pdfViewer.documentName}
        pageNumber={pdfViewer.pageNumber}
      />
    </div>
  )
}
